
import {
  Avatar,
  Box,
  BoxProps,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import {
  SaveTaskForm,
  StyledDisplayField
} from 'modules/components';
import { CustomIcon } from 'modules/components/layer00';
import React, { useState } from 'react';
import * as ds from './dataService';
import { DateTime } from 'luxon';

export type SaveTaskModalDetailSectionProps = {
  id: string;
  projectId: string;
  handleClose: () => void;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: SaveTaskModalDetailSectionProps & BoxProps) => {
  const theme = useTheme();
  const { data } = ds.useQuery(props.id ?? '');
  const [
    isEdit,
    setIsEdit
  ] = useState<boolean>(false);

  if (isEdit) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        width='31.25rem'
      >

        <SaveTaskForm.FC
          id={props.id}
          projectId={props.projectId}
          handleCancel={() => setIsEdit(() => false)}
        />
      </Box>
    );
  }
  return (
    <Box
      display='flex'
      paddingBottom='1.5rem'
      flexDirection='column'
      width='31.25rem'
      height='inherit'
      justifyContent='space-between'
    >
      <Stack
        rowGap='1.5rem'
      >
        {/* Header */}
        <Stack
          justifyContent='space-between'
          alignItems='center'
          direction='row'
          columnGap='1.25rem'
        >
          {/* ICONWITHBORDER */}
          <Stack
            height='3rem'
            width='3rem'
            alignItems='center'
            justifyContent='center'
            borderRadius='.75rem'
            border={`1px solid ${theme.palette.secondary.dark}`}
          >
            <CustomIcon.large.FC
              stroke={theme.palette.primary.main}
              icon='QUESTIONALT'
            />
          </Stack>
          <Stack
            flex='1 1 0'
            direction='column'
            justifyContent='space-between'
          >
            <Stack
              direction='row'
              justifyContent='space-between'
            >
              <Typography
                variant='modalTitle'
              >
                {data.title}
              </Typography>
              <IconButton
                onClick={() => props.handleClose()}
                sx={{
                  margin: 0,
                  padding: '0rem'
                }}
              >
                <CustomIcon.large.FC
                  icon='CLOSE'
                />
              </IconButton>
            </Stack>

            <Typography
              color={theme.palette.grey[500]}
              variant='formLabel'
            >
              Task Details
            </Typography>
          </Stack>

        </Stack>

        {/* INFO SECTION */}
        <Stack
          rowGap='.75rem'
        >
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                stroke={theme.palette.primary.main}
                icon='CALENDARALT'
              />
            )}
            label='Due'
          >
            <Typography
              variant='formInput'
            >
              {data.dueDate ? calculateDueDays(data.dueDate) : '-'}
            </Typography>

          </StyledDisplayField.FC>
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                stroke={theme.palette.primary.main}
                icon='AREA'
              />
            )}
            label='Area'
          >
            <Typography
              variant='formInput'
            >
              {data.area ? data.area : '-'}
            </Typography>

          </StyledDisplayField.FC>
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                stroke={theme.palette.primary.main}
                icon='TAG'
              />
            )}
            label='Category'
          >
            <Typography
              variant='formInput'
            >
              {data.category ? data.category : '-'}
            </Typography>

          </StyledDisplayField.FC>

          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                stroke={theme.palette.primary.main}
                icon='ACCOUNT'
              />
            )}
            label='Assigned To'
          >
            <Stack
              direction='row'
              columnGap='.25rem'
              alignItems='center'
              border={`1px solid ${theme.palette.secondary.dark}`}
              padding='.125rem'
              borderRadius='.5rem'
            >

              <Avatar
                sx={{
                  height: '1rem',
                  width: '1rem'
                }}
              />
              <Typography
                color={theme.palette.primary.main}
                variant='bodyBold'
              >
                {data.assignedTo ? data.assignedTo : '-'}
              </Typography>
            </Stack>

          </StyledDisplayField.FC>
        </Stack>

        {/* DESCRIPTION */}
        <Stack
          direction='column'
          rowGap='.75rem'
        >
          <Typography
            variant='formLabel'
          >
            Task Description
          </Typography>
          <Typography
            variant='formInput'
          >
            {data.description}
          </Typography>
        </Stack>

        {/* ATTACHMENTS */}
        <Stack
          direction='column'
        >
          {/* Attachement */}
          {data.attachments.map((rec, i) => (
            <Stack
              key={i}
              borderRadius='1rem'
              padding='1rem'
              bgcolor={theme.palette.secondary.main}
              direction='row'
              alignItems='center'
              columnGap='.58rem'
            >
              <CustomIcon.large.FC
                icon='FILE'
                stroke={theme.palette.primary.main}
              />
              <Stack
                flex='1 1 0'
                direction='column'
              >
                <Typography
                  variant='body1'
                >
                  {rec.fileName}
                </Typography>
                <Typography
                  color={theme.palette.grey[500]}
                  variant='body1'
                >
                  {rec.fileSize}
                </Typography>
              </Stack>
              <IconButton
                onClick={() => {
                  handleDownload(rec.url);
                }}
              >
                <CustomIcon.large.FC
                  stroke={theme.palette.primary.main}
                  icon='DOWLOAD'
                />
              </IconButton>

            </Stack>
          ))}
        </Stack>

      </Stack>
      <Box >

        {/* EDIT BOX */}
        <Stack
          border={`1px solid ${theme.palette.secondary.dark}`}
          borderRadius='1rem'
          direction='row'
          padding='.75rem'
          justifyContent='space-between'
          alignItems='center'
          marginBottom='1.5rem'

        >
          <Typography
            variant='bodyBold'
            color={theme.palette.primary.main}
          >
            Do you want to change something?
          </Typography>
          <Button
            sx={{
              color: theme.palette.primary.main,
              background: theme.palette.primary.light
            }}
            onClick={() => {
              setIsEdit((prev) => !prev);
            }}
            variant='contained'
            color='secondary'
          >
            Edit Task
          </Button>
        </Stack>
        {/* MODAL ACTIONS */}
        <Stack
          direction='row'
          columnGap='1rem'
        >
          <Button
            onClick={() => props.handleClose()}
            variant='outlined'
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            fullWidth
            onClick={() => {
              window.alert('Creating decision');
              props.handleClose();
            }}
          >
            Send to Approval
          </Button>
        </Stack>
      </Box>

    </Box>

  );

};

function calculateDueDays(dueDate: DateTime): React.ReactNode {
  const dueDays = dueDate?.diffNow().shiftTo('days').days;
  if (dueDays < 0) {
    return `${(dueDays * -1).toFixed(0)} days ago`;
  }

  return `${dueDays.toFixed(0)} days`;
}

const handleDownload = (url: string) => {
  window.open(url, '_blank');
};

